import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  AlarmClock,
  ArrowDownToLine,
  ArrowLeftRight,
  ArrowUpFromLine,
  Barcode,
  Boxes,
  Building2,
  CalendarClock,
  ClipboardCheck,
  ChevronDown,
  ClipboardList,
  Container,
  Factory,
  FileOutput,
  Forklift,
  Gauge,
  Grid3x3,
  Inbox,
  Layers,
  LayoutDashboard,
  LayoutGrid,
  Map as MapIcon,
  LogOut,
  MapPin,
  Package,
  PackageCheck,
  PackageMinus,
  PackagePlus,
  PanelLeftClose,
  PanelLeftOpen,
  ReceiptText,
  Route,
  Send,
  Shapes,
  ShoppingBasket,
  ShoppingCart,
  Store,
  ListChecks,
  Truck,
  Warehouse,
  type LucideIcon,
  HandCoins,
  Users,
  Wallet,
} from 'lucide-react';

import {
  Sidebar as SidebarShell,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarItem,
  SidebarTrigger,
  useSidebar,
} from '@/design-system/sidebar';
import { DashLogo } from '@/design-system/dash-logo';
import { cn } from '@/design-system/lib/utils';
import { toast } from '@/design-system/toaster';
import { useAuth } from '@/contexts/AuthContext';
import { SidebarProps } from '@/types/components';
import { APP_CONFIG } from '@/config/app';
import { WAREHOUSE_PATHS } from '@/pages/warehouse/paths';

/**
 * A nav node is either a leaf (has `router`) or a collapsible group (has
 * `children`). Any node may carry an icon; nested rows also indent by depth
 * (mockup: Warehouse → Outbound management → Goods issue → Picking).
 */
export interface NavItem {
  id: string;
  title: string;
  router?: string;
  icon?: LucideIcon;
  disabled?: boolean;
  children?: NavItem[];
}

interface NavigationGroup {
  label: string;
  items: NavItem[];
}

const NAVIGATION_GROUPS: NavigationGroup[] = [
  {
    label: 'Analytics',
    items: [
      {
        id: 'docking-dashboard',
        title: 'Docking Dashboard',
        router: '/dashboard',
        icon: LayoutDashboard,
      },
    ],
  },
  {
    label: 'Hub ops',
    items: [
      {
        id: 'shipments',
        title: 'Shipments',
        router: '/shipments',
        icon: Boxes,
      },
      { id: 'ujp', title: 'UJP', router: '/ujp', icon: Wallet },
      { id: 'packages', title: 'Packages', router: '/packages', icon: Package },
      { id: 'inbound', title: 'Inbound', router: '/inbound', icon: Inbox },
      {
        id: 'dispatches',
        title: 'Dispatches',
        router: '/dispatches',
        icon: Send,
      },
      {
        id: 'dispatch-schedules',
        title: 'Jadwal Dispatch',
        router: '/dispatch-schedules',
        icon: AlarmClock,
      },
      { id: 'delivery', title: 'Delivery', router: '/delivery', icon: Truck },
      {
        id: 'cod-orders',
        title: 'COD Reconciliation',
        router: '/cod-orders',
        icon: HandCoins,
      },
      {
        id: 'invoices',
        title: 'Invoices',
        router: '/invoices',
        icon: ReceiptText,
      },
    ],
  },
  {
    label: 'Warehouse ops',
    items: [
      {
        id: 'warehouse',
        title: 'Warehouse',
        icon: Warehouse,
        children: [
          {
            id: 'wh-dash',
            title: 'Dashboard',
            icon: Gauge,
            children: [
              {
                id: 'wh-stock',
                title: 'Stock',
                router: WAREHOUSE_PATHS.stock,
                icon: Layers,
              },
              {
                id: 'wh-storage-occupancy',
                title: 'Utilisasi lokasi',
                router: WAREHOUSE_PATHS.storageOccupancy,
                icon: Gauge,
              },
            ],
          },
          {
            id: 'wh-wm',
            title: 'Warehouse management',
            icon: LayoutGrid,
            children: [
              {
                id: 'wh-storage',
                title: 'Storage',
                router: WAREHOUSE_PATHS.storage,
                icon: Grid3x3,
              },
              {
                id: 'wh-map',
                title: 'Peta warehouse',
                router: WAREHOUSE_PATHS.warehouseMap,
                icon: MapIcon,
              },
              {
                id: 'wh-sku',
                title: 'SKU',
                router: WAREHOUSE_PATHS.sku,
                icon: Barcode,
              },
            ],
          },
          {
            id: 'wh-sc',
            title: 'Stock control',
            icon: ListChecks,
            children: [
              {
                id: 'wh-storage-transfer',
                title: 'Transfer storage',
                router: WAREHOUSE_PATHS.storageTransfer,
                icon: ArrowLeftRight,
              },
              {
                id: 'wh-hub-transfer',
                title: 'Transfer warehouse',
                router: WAREHOUSE_PATHS.hubTransfer,
                icon: Container,
              },
              {
                id: 'wh-stock-opname',
                title: 'Stock opname',
                router: WAREHOUSE_PATHS.stockOpname,
                icon: ClipboardCheck,
              },
            ],
          },
          {
            id: 'wh-ob',
            title: 'Outbound management',
            icon: ArrowUpFromLine,
            children: [
              {
                id: 'wh-outbound-request',
                title: 'Outbound request',
                router: WAREHOUSE_PATHS.outboundRequest,
                icon: ClipboardList,
              },
              {
                id: 'wh-gi',
                title: 'Goods issue',
                icon: PackageMinus,
                children: [
                  {
                    id: 'wh-picking',
                    title: 'Picking',
                    router: WAREHOUSE_PATHS.picking,
                    icon: ShoppingBasket,
                  },
                  {
                    id: 'wh-shipping',
                    title: 'Shipping',
                    router: WAREHOUSE_PATHS.shipping,
                    icon: Forklift,
                  },
                ],
              },
            ],
          },
          {
            id: 'wh-sm',
            title: 'Supply management',
            icon: Factory,
            children: [
              {
                id: 'wh-purchase-order',
                title: 'Purchase order',
                router: WAREHOUSE_PATHS.purchaseOrder,
                icon: ShoppingCart,
              },
              {
                id: 'wh-vendor',
                title: 'Vendor',
                router: WAREHOUSE_PATHS.vendor,
                icon: Store,
              },
            ],
          },
          {
            id: 'wh-ib',
            title: 'Inbound management',
            icon: ArrowDownToLine,
            children: [
              {
                id: 'wh-inbound-request',
                title: 'Inbound request',
                router: WAREHOUSE_PATHS.inboundRequest,
                icon: PackagePlus,
              },
              {
                id: 'wh-goods-receipt',
                title: 'Goods receipt',
                router: WAREHOUSE_PATHS.goodsReceipt,
                icon: PackageCheck,
              },
            ],
          },
        ],
      },
    ],
  },
  {
    label: 'Scheduled Instant',
    items: [
      {
        id: 'scheduled-instant',
        title: 'Schedule',
        router: '/scheduled-instant',
        icon: CalendarClock,
      },
    ],
  },
  {
    label: 'Master',
    items: [
      { id: 'providers', title: 'Clients', router: '/providers', icon: Users },
      { id: 'hubs', title: 'Hubs', router: '/hubs', icon: Building2 },
      { id: 'zones', title: 'Zones', router: '/zones', icon: Shapes },
      {
        id: 'workflows',
        title: 'Workflows',
        router: '/workflows',
        icon: ListChecks,
      },
      {
        id: 'addresses',
        title: 'Addresses',
        router: '/addresses',
        icon: MapPin,
      },
      {
        id: 'route-planner',
        title: 'Route Planner',
        router: '/route-planner',
        icon: Route,
      },
      {
        id: 'export-templates',
        title: 'Export Templates',
        router: '/export-templates',
        icon: FileOutput,
      },
    ],
  },
];

export const isItemActive = (item: NavItem, currentPath: string): boolean => {
  if (!item.router) return false;
  return item.router === '/'
    ? currentPath === '/'
    : currentPath === item.router || currentPath.startsWith(`${item.router}/`);
};

/** True when the node or any descendant is the active route. */
export const containsActive = (item: NavItem, currentPath: string): boolean =>
  item.children
    ? item.children.some((c) => containsActive(c, currentPath))
    : isItemActive(item, currentPath);

/** Ids of every group on the path to the active leaf. */
const activeBranchIds = (items: NavItem[], currentPath: string): string[] =>
  items.flatMap((i) =>
    i.children && containsActive(i, currentPath)
      ? [i.id, ...activeBranchIds(i.children, currentPath)]
      : []
  );

/** Left padding per depth so nested rows line up under the parent label. */
const INDENT = ['', 'pl-[49px]', 'pl-[61px]', 'pl-[73px]'];
const NESTED = 'h-8 gap-2 pr-3 text-[13px]';

interface NavTreeProps {
  items: NavItem[];
  depth: number;
  currentPath: string;
  isCollapsed: boolean;
  open: Record<string, boolean>;
  onToggle: (id: string) => void;
}

const NavTree: React.FC<NavTreeProps> = ({
  items,
  depth,
  currentPath,
  isCollapsed,
  open,
  onToggle,
}) => (
  <>
    {items.map((item) => {
      const Icon = item.icon;
      const indent = cn(INDENT[Math.min(depth, 3)], depth > 0 && NESTED);

      if (item.children) {
        const expanded = open[item.id] ?? false;
        const branchActive = containsActive(item, currentPath);
        return (
          <React.Fragment key={item.id}>
            <SidebarItem
              type="button"
              aria-expanded={expanded}
              data-nav-group={item.id}
              title={isCollapsed ? item.title : undefined}
              // An icon-only rail button has no text to name it.
              aria-label={isCollapsed ? item.title : undefined}
              // Collapsed rail: the root carries the active rail for its branch.
              active={isCollapsed && branchActive}
              onClick={() => onToggle(item.id)}
              className={cn(
                indent,
                branchActive && 'text-text-strong-950',
                isCollapsed && 'justify-center px-0'
              )}
            >
              {Icon && <Icon strokeWidth={1.75} />}
              {!isCollapsed && (
                <>
                  <span className="flex-1 truncate text-left">
                    {item.title}
                  </span>
                  <ChevronDown
                    aria-hidden
                    strokeWidth={1.75}
                    className={cn(
                      '!size-3.5 text-icon-soft-400 transition-transform',
                      expanded ? 'rotate-0' : '-rotate-90'
                    )}
                  />
                </>
              )}
            </SidebarItem>
            {expanded && !isCollapsed && (
              <NavTree
                items={item.children}
                depth={depth + 1}
                currentPath={currentPath}
                isCollapsed={isCollapsed}
                open={open}
                onToggle={onToggle}
              />
            )}
          </React.Fragment>
        );
      }

      const active = isItemActive(item, currentPath);
      return (
        <SidebarItem
          key={item.id}
          asChild
          active={active}
          className={cn(
            indent,
            isCollapsed && 'justify-center px-0',
            item.disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
          )}
        >
          <Link
            to={item.disabled || !item.router ? '#' : item.router}
            title={isCollapsed ? item.title : undefined}
            aria-label={isCollapsed ? item.title : undefined}
            aria-current={active ? 'page' : undefined}
            onClick={(e) => item.disabled && e.preventDefault()}
          >
            {Icon && <Icon strokeWidth={1.75} />}
            {!isCollapsed && (
              <span className="flex-1 truncate">{item.title}</span>
            )}
          </Link>
        </SidebarItem>
      );
    })}
  </>
);

/** Every group starts open except top-level roots off the active branch. */
const initialOpen = (currentPath: string): Record<string, boolean> => {
  const out: Record<string, boolean> = {};
  const walk = (items: NavItem[], depth: number) =>
    items.forEach((i) => {
      if (!i.children) return;
      out[i.id] = depth > 0 || containsActive(i, currentPath);
      walk(i.children, depth + 1);
    });
  NAVIGATION_GROUPS.forEach((g) => walk(g.items, 0));
  return out;
};

export const Sidebar: React.FC<SidebarProps> = ({
  currentPath = APP_CONFIG.homeRoute,
}) => {
  const { user, signOut } = useAuth();
  const { state, setState } = useSidebar();
  const isCollapsed = state === 'collapsed';
  const [open, setOpen] = useState<Record<string, boolean>>(() =>
    initialOpen(currentPath)
  );

  // Auto-expand the branch holding the active route on every navigation.
  const branch = useMemo(
    () =>
      NAVIGATION_GROUPS.flatMap((g) => activeBranchIds(g.items, currentPath)),
    [currentPath]
  );
  useEffect(() => {
    if (!branch.length) return;
    setOpen((prev) =>
      branch.every((id) => prev[id])
        ? prev
        : { ...prev, ...Object.fromEntries(branch.map((id) => [id, true])) }
    );
  }, [branch]);

  const toggleGroup = (id: string) => {
    // On the icon rail a group can't unfold in place — expand the sidebar
    // and open the group instead.
    if (isCollapsed) {
      setState('expanded');
      setOpen((prev) => ({ ...prev, [id]: true }));
      return;
    }
    setOpen((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      toast.error('Could not sign out. Please try again.');
    }
  };

  const avatarSrc =
    user?.photoURL ||
    `https://ui-avatars.com/api/?name=${encodeURIComponent(
      user?.displayName || user?.email || 'User'
    )}&background=5E2AAC&color=fff`;

  const userLabel = user?.displayName || user?.email || 'User';

  // One tree for both states — the kit's `collapsedWidth="icon"` variant
  // narrows the shell to 64px and we drop the labels. Active state, hover
  // and the accent rail stay owned by <SidebarItem> in either width.
  return (
    <SidebarShell
      // 15rem is the kit's GSM default. The old 13.5rem left the header
      // ~10px short once the toggle joined the lockup, wrapping "Dash
      // Electric" onto two lines.
      width="15rem"
      collapsedWidth="icon"
      className="data-[state=collapsed]:overflow-x-hidden"
    >
      {/* Expanded: logo lockup with the toggle trailing it. Collapsed: the
          rail is too narrow to sit them side by side, so the toggle stacks
          under the mark and the header grows past its default 64px. */}
      <SidebarHeader
        className={isCollapsed ? 'h-auto flex-col gap-2 px-0 py-3' : 'gap-2.5'}
      >
        {/* eslint-disable-next-line react/style-prop-object -- DashLogo `style` is a color-variant prop, not the DOM style attribute */}
        <DashLogo variant="mark" style="black" size="sm" />
        {!isCollapsed && (
          // nowrap so the lockup clips rather than reflowing to two lines
          // during the 200ms width transition out of the collapsed rail.
          <div className="overflow-hidden">
            <p className="whitespace-nowrap text-[17px] font-extrabold tracking-tight text-text-strong-950 leading-none">
              Dash Electric
            </p>
            <p className="mt-1 whitespace-nowrap text-[8px] font-semibold uppercase tracking-[0.3em] text-text-sub-600 leading-none">
              Mithril
            </p>
          </div>
        )}
        <SidebarTrigger
          aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          aria-expanded={!isCollapsed}
          title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          className={isCollapsed ? 'rounded-gsm' : 'ml-auto rounded-gsm'}
        >
          {isCollapsed ? (
            <PanelLeftOpen size={18} />
          ) : (
            <PanelLeftClose size={18} />
          )}
        </SidebarTrigger>
      </SidebarHeader>

      <SidebarContent>
        {NAVIGATION_GROUPS.map((group) => (
          <SidebarGroup key={group.label}>
            {!isCollapsed && (
              <SidebarGroupLabel>{group.label}</SidebarGroupLabel>
            )}
            <NavTree
              items={group.items}
              depth={0}
              currentPath={currentPath}
              isCollapsed={isCollapsed}
              open={open}
              onToggle={toggleGroup}
            />
          </SidebarGroup>
        ))}
      </SidebarContent>

      {user && (
        <SidebarFooter
          className={
            isCollapsed ? 'h-auto flex-col gap-2 px-0 py-3' : 'h-auto px-3 py-3'
          }
        >
          <div
            className={
              isCollapsed
                ? 'flex flex-col items-center gap-2'
                : 'flex w-full items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-bg-weak-50 transition-colors'
            }
          >
            <div className="relative shrink-0">
              <img
                src={avatarSrc}
                alt={userLabel}
                title={isCollapsed ? userLabel : undefined}
                className="w-8 h-8 rounded-full"
              />
              <span
                className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-(--state-success-base) border-2 border-bg-white-0"
                aria-hidden="true"
              />
            </div>
            {!isCollapsed && (
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-semibold tracking-tight text-text-strong-950 truncate leading-tight">
                  {user.displayName || user.firstName || 'User'}
                </p>
                <p className="text-[11px] text-text-sub-600 truncate leading-tight">
                  {user.email}
                </p>
              </div>
            )}
            <button
              type="button"
              onClick={handleLogout}
              className="p-1.5 rounded-lg hover:bg-(--state-error-lighter) transition-colors text-icon-soft-400 hover:text-(--state-error-base)"
              title="Sign out"
              aria-label="Sign out"
            >
              <LogOut size={16} />
            </button>
          </div>
        </SidebarFooter>
      )}
    </SidebarShell>
  );
};
